import React, { useState } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { CalendarIcon, ChevronLeft, ChevronRight, X as XIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './Button';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

function toDate(value: string): Date | undefined {
    if (!value) return undefined;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return undefined;
    return new Date(year, month - 1, day);
}

function toValue(date: Date | undefined): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatLabel(date: Date): string {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

interface DateRangePickerProps {
    from: string;
    to: string;
    onChange: (range: { from: string; to: string }) => void;
    placeholder?: string;
    className?: string;
}

export function DateRangePicker({ from, to, onChange, placeholder = 'Pick a date range', className }: DateRangePickerProps) {
    const [open, setOpen] = useState(false);
    const selected: DateRange | undefined = (toDate(from) || toDate(to))
        ? { from: toDate(from), to: toDate(to) }
        : undefined;

    const handleSelect = (range: DateRange | undefined) => {
        onChange({ from: toValue(range?.from), to: toValue(range?.to) });
    };

    const label = selected?.from
        ? selected.to && selected.to.getTime() !== selected.from.getTime()
            ? `${formatLabel(selected.from)} – ${formatLabel(selected.to)}`
            : formatLabel(selected.from)
        : placeholder;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex h-10 items-center gap-2 whitespace-nowrap rounded-lg border border-zinc-950/10 bg-transparent px-3.5 py-2.5 text-base/6 shadow-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring dark:border-white/10 sm:py-1.5 sm:text-sm/6',
                        selected?.from ? 'text-zinc-950 dark:text-white' : 'text-zinc-500 dark:text-zinc-400',
                        className
                    )}
                >
                    <CalendarIcon className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400" />
                    <span className="line-clamp-1">{label}</span>
                    {selected?.from && (
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange({ from: '', to: '' });
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                    onChange({ from: '', to: '' });
                                }
                            }}
                            className="ml-auto rounded-md p-0.5 text-zinc-400 hover:bg-zinc-950/5 hover:text-zinc-600 dark:hover:bg-white/10 dark:hover:text-zinc-200"
                            aria-label="Clear date range"
                        >
                            <XIcon className="size-3.5" />
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="start">
                <DayPicker
                    mode="range"
                    numberOfMonths={2}
                    selected={selected}
                    onSelect={handleSelect}
                    defaultMonth={selected?.from}
                    showOutsideDays
                    classNames={{
                        months: 'flex flex-col sm:flex-row gap-4',
                        month: 'space-y-3',
                        caption: 'flex justify-center pt-1 relative items-center',
                        caption_label: 'text-sm font-medium text-zinc-950 dark:text-white',
                        nav: 'space-x-1 flex items-center',
                        nav_button: 'size-7 inline-flex items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-950/5 dark:text-zinc-400 dark:hover:bg-white/10',
                        nav_button_previous: 'absolute left-1',
                        nav_button_next: 'absolute right-1',
                        table: 'w-full border-collapse',
                        head_row: 'flex',
                        head_cell: 'text-zinc-500 dark:text-zinc-400 rounded-md w-9 font-normal text-xs',
                        row: 'flex w-full mt-1',
                        cell: 'text-center text-sm p-0 relative [&:has([aria-selected])]:bg-primary/10 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md',
                        day: 'size-9 p-0 font-normal rounded-md hover:bg-zinc-950/5 dark:hover:bg-white/10 aria-selected:opacity-100',
                        day_selected: 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
                        day_today: 'font-semibold text-primary',
                        day_outside: 'text-zinc-400 opacity-50 dark:text-zinc-500',
                        day_disabled: 'text-zinc-400 opacity-50 dark:text-zinc-500',
                        day_range_middle: 'aria-selected:bg-primary/10 aria-selected:text-zinc-950 dark:aria-selected:text-white rounded-none',
                        day_range_start: 'rounded-r-none',
                        day_range_end: 'rounded-l-none',
                        day_hidden: 'invisible',
                    }}
                    components={{
                        IconLeft: () => <ChevronLeft className="size-4" />,
                        IconRight: () => <ChevronRight className="size-4" />,
                    }}
                />
                <div className="mt-2 flex justify-end border-t border-zinc-950/5 pt-3 dark:border-white/10">
                    <Button plain onClick={() => setOpen(false)}>Done</Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
